import React, { useEffect } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useContentToken } from '@vibesites/runtime/react';
import { useScrollReveal } from '@vibesites/theme-preferred/hooks/useScrollReveal';
import { useRevealStyles } from '@vibesites/theme-preferred/hooks/useRevealStyles';

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function cellText(cell: Element | undefined, label: string): string {
  if (!cell) return '';
  const raw = (cell.textContent || '').replace(/\s+/g, ' ').trim();
  return raw.replace(new RegExp(`^${label}\\s*`, 'i'), '').trim();
}

function priceText(root: ParentNode | null | undefined, selector: string): string {
  const el = root?.querySelector(selector);
  if (!el) return '';
  return Array.from(el.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent || '')
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

function appendRow(list: HTMLElement, label: string, value: string) {
  if (!value) return;
  const item = document.createElement('li');
  const row = document.createElement('div');
  row.className = 'fee-row';
  const name = document.createElement('span');
  name.className = 'fee-name';
  name.textContent = label;
  const amount = document.createElement('span');
  amount.className = 'fee-amount';
  amount.textContent = value;
  row.append(name, amount);
  item.append(row);
  list.append(item);
}

/**
 * ApartmentSync renders "See Fee Details" and then copies `#<unitId>` into
 * the fee dialog. That hidden node is empty for this property, so the dialog
 * opens as a blank box. Fill it from the unit row before the vendor handler
 * copies it.
 */
function buildFeeDetails(trigger: Element, apartmentId: string): HTMLElement {
  const row = trigger.closest('tr');
  const unit = row?.querySelector('.unitNumber')?.textContent?.trim() || 'This home';
  const allIn = priceText(row, '.unit-rrac-price') || priceText(row, '.rrac-price');
  const baseRaw = row?.querySelector('.unit_price_value_base')?.textContent || '';
  const base = baseRaw.replace(/base rent starting from/i, '').replace(/\s+/g, ' ').trim();
  const cells = row ? Array.from(row.children) : [];
  const deposit = cellText(cells[5], 'Deposit');
  const available = cellText(cells[6], 'Availability');
  const lease = document.getElementById(`goto_lease_${apartmentId}`);
  const leaseHref = lease instanceof HTMLAnchorElement ? lease.href : '';

  const wrapper = document.createElement('div');
  wrapper.className = 'fee-wrapper';

  const heading = document.createElement('h4');
  heading.textContent = `Fee details for ${unit}`;
  wrapper.append(heading);

  const subwrapper = document.createElement('div');
  subwrapper.className = 'fee-subwrapper';
  const sub = document.createElement('div');
  sub.className = 'fee-sub';

  const summary = document.createElement('div');
  summary.className = 'fee-calc';
  const summaryTitle = document.createElement('h2');
  summaryTitle.textContent = 'Monthly price';
  summary.append(summaryTitle);
  if (allIn) {
    const summaryValue = document.createElement('span');
    summaryValue.className = 'fee-calc-sub';
    summaryValue.textContent = allIn;
    summary.append(summaryValue);
  }
  sub.append(summary);

  const list = document.createElement('ul');
  list.className = 'fee-list';
  appendRow(list, 'All-in monthly price', allIn);
  appendRow(list, 'Base rent', base);
  appendRow(list, 'Deposit', deposit);
  appendRow(list, 'Available', available);
  sub.append(list);
  subwrapper.append(sub);
  wrapper.append(subwrapper);

  const note = document.createElement('p');
  note.textContent = 'Includes all required monthly fees. Parking, pets, and other add-ons are confirmed when you continue.';
  note.style.margin = '12px 0 0';
  note.style.fontSize = '14px';
  note.style.lineHeight = '1.5';
  note.style.color = '#414042';
  wrapper.append(note);

  const leasing = document.createElement('div');
  leasing.className = 'unit-leaseing';
  if (leaseHref) {
    const link = document.createElement('a');
    link.href = leaseHref;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = 'Continue with these fees';
    leasing.append(link);
  }
  wrapper.append(leasing);

  return wrapper;
}

function hiddenFeeHost(apartmentId: string): HTMLElement | null {
  const existing = document.getElementById(apartmentId);
  if (existing) return existing;
  return null;
}

function hostIsEmpty(host: HTMLElement): boolean {
  if (host.querySelector('iframe[src], .fee-list li, .fee-calc')) return false;
  return !(host.textContent || '').trim();
}

function ensureFeeDialogContent(trigger: Element) {
  const apartmentId = trigger.getAttribute('data-apartment');
  if (!apartmentId) return;
  const host = hiddenFeeHost(apartmentId);
  if (!host || !hostIsEmpty(host)) return;
  host.replaceChildren(buildFeeDetails(trigger, apartmentId));
}

function useFeeDetailsFallback() {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const trigger = target.closest('.fee_details');
      if (!trigger) return;
      ensureFeeDialogContent(trigger);

      window.setTimeout(() => {
        const dialog = document.getElementById('fee_dialog');
        if (!dialog || !hostIsEmpty(dialog)) return;
        const apartmentId = trigger.getAttribute('data-apartment');
        if (!apartmentId) return;
        dialog.replaceChildren(buildFeeDetails(trigger, apartmentId));
        dialog.style.height = 'auto';
        dialog.style.color = '#414042';
        const shell = dialog.closest('.ui-dialog');
        if (shell instanceof HTMLElement) shell.style.height = 'auto';
      }, 80);
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);
}

/**
 * Short pricing disclaimer / verbiage shown above the floor-plans
 * ApartmentSync embed. Bound to `custom_pricing_verbiage`; hides itself
 * when the field is empty.
 */
export function PricingVerbiage() {
  useRevealStyles();
  useFeeDetailsFallback();
  const sectionRef = useScrollReveal<HTMLElement>();

  const pricingVerbiage = useContentToken('pricingverbiage.text');

  if (!hasValue(pricingVerbiage)) return null;

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="reveal-fade-up pt-8 pb-1 md:pt-12 md:pb-2 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="pricingverbiage.text"
          as="p"
          className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)]"
        />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PricingVerbiage',
  displayName: 'Pricing Verbiage',
  tokens: {
    'pricingverbiage.text': {
      label: 'Pricing Verbiage',
      type: 'richtext',
      defaultField: 'custom_pricing_verbiage',
    },
  },
};

(PricingVerbiage as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PricingVerbiage;
